import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Radio, Select, Space } from 'antd';
import { publicRuntimeConfig } from 'config/index';
import { Formik } from 'formik';

import { addErrorMessage, FormField, FormikFieldTouched } from 'components/common';
import { defaultFilterOptions } from 'components/common/utils';
import { classnames } from 'lib/classnames';
import { ITEM_LINK_TYPE_VALUE } from 'lib/constants/itemLink';
import { CHARACTER_SIZE, MAX_SYSTEM_NAME_LENGTH } from 'lib/global';
import { useConstantConfig } from 'lib/hooks';
import useI18n from 'lib/hooks/useI18n';
import { ItemLinkType } from 'lib/models';
import Parse from 'lib/parse';
import { nameVerify } from 'lib/regExp';
import { FormError } from 'lib/types/error';
import { ItemLinkType as ItemLinkTypeProps, ItemType } from 'lib/types/models';

import styles from './LinkModal.module.less';

const { TextArea } = Input;

const cx = classnames(styles);
interface ModalProps {
  itemTypeList: ItemType[];
  visible: boolean;
  loading: boolean;
  onSubmit: (values: ItemLinkTypeProps) => void;
  onCancel: () => void;
  initialValues?: ItemLinkTypeProps;
  width: number;
  title: string;
}
const { Option } = Select;

const LinkModal: React.FC<ModalProps> = ({
  itemTypeList,
  visible,
  onSubmit,
  onCancel,
  loading,
  initialValues,
  width,
  title,
}) => {
  const { t } = useI18n();
  const { isDemo } = publicRuntimeConfig;
  const form = useRef(null);
  const [isStepOne, setIsStepOne] = useState(true);
  const [inwardItemType, setInwardItemType] = useState(ITEM_LINK_TYPE_VALUE.ALL);
  const [outwardItemType, setOutwardItemType] = useState(ITEM_LINK_TYPE_VALUE.ALL);
  const [modalTitle, setModalTitle] = useState('');
  const [type, setType] = useState(ITEM_LINK_TYPE_VALUE.ONE_TO_MANY);
  const { NAME_LENGTH_ERROR } = useConstantConfig();

  const initialValue = useMemo(() => {
    return {
      objectId: initialValues?.objectId || '',
      type: initialValues?.type || ITEM_LINK_TYPE_VALUE.ONE_TO_MANY,
      name: initialValues?.name || '',
      inward: initialValues?.inward || '',
      outward: initialValues?.outward || '',
      inwardItemType: initialValues?.inwardItemType || ITEM_LINK_TYPE_VALUE.ALL,
      outwardItemType: initialValues?.outwardItemType || ITEM_LINK_TYPE_VALUE.ALL,
      inwardItemTypeMappings: initialValues?.inwardItemTypeMappings || [],
      outwardItemTypeMappings: initialValues?.outwardItemTypeMappings || [],
      inwardIql: initialValues?.inwardIql || '',
      outwardIql: initialValues?.outwardIql || '',
    };
  }, [initialValues]);

  useEffect(() => {
    setModalTitle(title);
  }, [title]);

  useEffect(() => {
    if (initialValues?.objectId) {
      setIsStepOne(false);
    }
    setInwardItemType(initialValues?.inwardItemType || ITEM_LINK_TYPE_VALUE.ALL);
    setOutwardItemType(initialValues?.outwardItemType || ITEM_LINK_TYPE_VALUE.ALL);
  }, [initialValues]);

  const validateOnBlur = useCallback(
    async e => {
      e.preventDefault();
      const value = e.target?.value;
      const errorName = nameVerify(value);
      if (
        value.length < CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH ||
        value.length > CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH
      ) {
        form.current.setFieldError('name', NAME_LENGTH_ERROR);
      } else if (errorName) {
        form.current.setFieldError('name', errorName);
      } else {
        const query = new Parse.Query(ItemLinkType);
        const findName = await query.equalTo('name', value.trim()).find();
        if (findName.length && initialValues?.name !== value) {
          form.current.setFieldError('name', t('validate.item.itemRelateNameExist'));
        } else {
          form.current.setFieldError('name', null);
          delete form.current.errors.name;
        }
      }
    },
    [form, initialValues, t],
  );

  const inwardValidate = useCallback(
    async e => {
      e.preventDefault();
      const { value } = e.target;
      if (
        value.length < CHARACTER_SIZE.MIN_SYSTEM_ITEM_LENGTH ||
        value.length > CHARACTER_SIZE.MAX_SYSTEM_ITEM_LENGTH
      ) {
        form.current.setFieldError(
          'inward',
          t('validate.item.inward.desc', {
            min: CHARACTER_SIZE.MIN_SYSTEM_ITEM_LENGTH,
            max: CHARACTER_SIZE.MAX_SYSTEM_ITEM_LENGTH,
          }),
        );
      } else {
        const query = new Parse.Query(ItemLinkType);
        const findInward = await query.equalTo('inward', value.trim()).find();
        if (findInward.length && initialValues?.inward !== value) {
          form.current.setFieldError('inward', t('validate.item.inward.exist'));
        } else {
          form.current.setFieldError('inward', null);
          delete form.current.errors.inward;
        }
      }
    },
    [form, initialValues, t],
  );

  const outwardValidate = useCallback(
    async e => {
      e.preventDefault();
      const { value } = e.target;
      if (
        value.length < CHARACTER_SIZE.MIN_SYSTEM_ITEM_LENGTH ||
        value.length > CHARACTER_SIZE.MAX_SYSTEM_ITEM_LENGTH
      ) {
        form.current.setFieldError(
          'outward',
          t('validate.item.inward.desc', {
            min: CHARACTER_SIZE.MIN_SYSTEM_ITEM_LENGTH,
            max: CHARACTER_SIZE.MAX_SYSTEM_ITEM_LENGTH,
          }),
        );
      } else {
        const query = new Parse.Query(ItemLinkType);
        const findOutward = await query.equalTo('outward', value.trim()).find();
        if (findOutward.length && initialValues?.outward !== value) {
          form.current.setFieldError('outward', t('validate.item.inward.exist'));
        } else {
          form.current.setFieldError('outward', null);
          delete form.current.errors.outward;
        }
      }
    },
    [form, initialValues, t],
  );

  const validate = async value => {
    const errors = {} as FormError;
    if (!value.type) {
      errors.type = addErrorMessage(errors.type, t('validate.item.type'));
    }
    if (!value.name) {
      errors.name = addErrorMessage(errors.name, t('validate.item.name'));
    } else if (form.current.errors?.name) {
      errors.name = form.current.errors.name;
    }
    if (!value.inward) {
      errors.inward = addErrorMessage(errors.inward, t('validate.item.inwardDesc'));
    } else if (form.current.errors?.inward) {
      errors.inward = form.current.errors.inward;
    }
    if (!value.outward) {
      errors.outward = addErrorMessage(errors.outward, t('validate.item.outward'));
    } else if (form.current.errors?.outward) {
      errors.outward = form.current.errors.outward;
    }
    if (value.inwardItemType === ITEM_LINK_TYPE_VALUE.SOME && !value.inwardItemTypeMappings?.length) {
      errors.inwardItemTypeMappings = addErrorMessage(
        errors.inwardItemTypeMappings,
        t('validate.item.inwardItemTypeMappings'),
      );
    }
    if (value.outwardItemType === ITEM_LINK_TYPE_VALUE.SOME && !value.outwardItemTypeMappings?.length) {
      errors.outwardItemTypeMappings = addErrorMessage(
        errors.outwardItemTypeMappings,
        t('validate.item.inwardItemTypeMappings'),
      );
    }

    return errors;
  };

  return (
    <Formik innerRef={form} initialValues={initialValue} onSubmit={onSubmit} validate={validate}>
      {({ handleSubmit }) => (
        <Modal
          maskClosable={false}
          className={cx('modal')}
          confirmLoading={loading}
          width={width}
          title={modalTitle}
          open={visible}
          onOk={() => handleSubmit()}
          onCancel={onCancel}
          footer={
            isStepOne
              ? [
                  <Button key="back" onClick={onCancel}>
                    {t('global.cancel')}
                  </Button>,
                  <Button
                    key="next"
                    type="primary"
                    disabled={isDemo}
                    onClick={() => {
                      setIsStepOne(false);

                      setModalTitle(
                        type === ITEM_LINK_TYPE_VALUE.ONE_TO_MANY
                          ? t('pages.LinkModal.modal.title')
                          : t('pages.LinkModal.modal.title1'),
                      );
                    }}
                  >
                    {t('global.nextStep')}
                  </Button>,
                ]
              : initialValues?.objectId
                ? [
                    <Button key="back" onClick={onCancel}>
                      {t('global.cancel')}
                    </Button>,
                    <Button
                      key="submit"
                      type="primary"
                      loading={loading}
                      disabled={isDemo}
                      onClick={() => handleSubmit()}
                    >
                      {t('global.confirm')}
                    </Button>,
                  ]
                : [
                    <Button
                      key="pre"
                      onClick={() => {
                        setIsStepOne(true);
                        setModalTitle(t('pages.LinkModal.modal.btnTitle'));
                      }}
                    >
                      {t('global.preStep')}
                    </Button>,
                    <Button
                      key="submit"
                      type="primary"
                      disabled={isDemo}
                      loading={loading}
                      onClick={() => handleSubmit()}
                    >
                      {t('global.confirm')}
                    </Button>,
                  ]
          }
        >
          <FormikFieldTouched />
          {isStepOne ? (
            <FormField name="type" label={t('pages.LinkModal.FormField.type')} required>
              {({ field }) => (
                <Radio.Group
                  className={cx('step-one-group')}
                  {...field}
                  onChange={e => {
                    field.onChange(e.target.value);
                    setType(e.target.value);
                  }}
                >
                  <Space direction="vertical">
                    <Radio value={ITEM_LINK_TYPE_VALUE.ONE_TO_MANY}>
                      <div>
                        <p>{t('pages.LinkModal.oneToMany.title.one')}</p>
                        <p className={cx('tip')}>{t('pages.LinkModal.oneToMany.tip.one')}</p>
                      </div>
                    </Radio>
                    <Radio value={ITEM_LINK_TYPE_VALUE.MANY_TO_MANY}>
                      <div>
                        <p>{t('pages.LinkModal.oneToMany.title.many')}</p>
                        <p className={cx('tip')}>{t('pages.LinkModal.oneToMany.tip.many')}</p>
                      </div>
                    </Radio>
                  </Space>
                </Radio.Group>
              )}
            </FormField>
          ) : (
            <>
              <FormField name="name" label={t('pages.LinkModal.FormField.name')} required>
                {({ field }) => (
                  <Input
                    maxLength={MAX_SYSTEM_NAME_LENGTH}
                    placeholder={t('pages.LinkModal.placeholder.relateItem')}
                    {...field}
                    onBlur={validateOnBlur}
                  />
                )}
              </FormField>
              <FormField name="inward" label={t('pages.LinkModal.FormField.inward')} required>
                {({ field }) => (
                  <Input
                    maxLength={60}
                    placeholder={t('pages.LinkModal.placeholder.inward')}
                    {...field}
                    onBlur={inwardValidate}
                  />
                )}
              </FormField>
              <FormField
                name="inwardItemType"
                label={t('pages.LinkModal.FormField.inwardConfig')}
                required
                className={cx({
                  'link-form-field': [ITEM_LINK_TYPE_VALUE.SOME, ITEM_LINK_TYPE_VALUE.IQL].includes(inwardItemType),
                })}
              >
                {({ field }) => (
                  <Radio.Group
                    {...field}
                    onChange={e => {
                      field.onChange(e.target.value);
                      setInwardItemType(e.target.value);
                    }}
                  >
                    <Radio value={ITEM_LINK_TYPE_VALUE.ALL}>{t('pages.LinkModal.FormField.all')}</Radio>
                    <Radio value={ITEM_LINK_TYPE_VALUE.SOME}>{t('pages.LinkModal.FormField.some')}</Radio>
                    <Radio value={ITEM_LINK_TYPE_VALUE.IQL}>{t('pages.LinkModal.FormField.iql')}</Radio>
                  </Radio.Group>
                )}
              </FormField>
              {inwardItemType === ITEM_LINK_TYPE_VALUE.SOME && (
                <FormField name="inwardItemTypeMappings" label="">
                  {({ field }) => (
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      filterOption={defaultFilterOptions}
                      placeholder={t('pages.LinkModal.placeholder.itemType')}
                      {...field}
                    >
                      {itemTypeList.map(ele => {
                        return (
                          <Option key={ele.objectId} value={ele.objectId}>
                            {ele.name}
                          </Option>
                        );
                      })}
                    </Select>
                  )}
                </FormField>
              )}
              {inwardItemType === ITEM_LINK_TYPE_VALUE.IQL && (
                <FormField name="inwardIql" label="">
                  {({ field }) => (
                    <TextArea {...field} rows={2} placeholder={t('pages.LinkModal.FormField.iqlPlaceholder')} />
                  )}
                </FormField>
              )}
              <FormField name="outward" label={t('pages.LinkModal.FormField.outward')} required>
                {({ field }) => (
                  <Input
                    maxLength={60}
                    placeholder={t('pages.LinkModal.placeholder.outward')}
                    {...field}
                    onBlur={outwardValidate}
                  />
                )}
              </FormField>
              <FormField
                name="outwardItemType"
                label={t('pages.LinkModal.FormField.outwardConfig')}
                required
                className={cx({
                  'link-form-field': [ITEM_LINK_TYPE_VALUE.SOME, ITEM_LINK_TYPE_VALUE.IQL].includes(outwardItemType),
                })}
              >
                {({ field }) => (
                  <Radio.Group
                    {...field}
                    onChange={e => {
                      field.onChange(e.target.value);
                      setOutwardItemType(e.target.value);
                    }}
                  >
                    <Radio value={ITEM_LINK_TYPE_VALUE.ALL}>{t('pages.LinkModal.FormField.all')}</Radio>
                    <Radio value={ITEM_LINK_TYPE_VALUE.SOME}>{t('pages.LinkModal.FormField.some')}</Radio>
                    <Radio value={ITEM_LINK_TYPE_VALUE.IQL}>{t('pages.LinkModal.FormField.iql')}</Radio>
                  </Radio.Group>
                )}
              </FormField>
              {outwardItemType === ITEM_LINK_TYPE_VALUE.SOME && (
                <FormField name="outwardItemTypeMappings" label="">
                  {({ field }) => (
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      filterOption={defaultFilterOptions}
                      placeholder={t('pages.itemTypes.validate.itemType')}
                      {...field}
                    >
                      {itemTypeList.map(ele => {
                        return (
                          <Option key={ele.objectId} value={ele.objectId}>
                            {ele.name}
                          </Option>
                        );
                      })}
                    </Select>
                  )}
                </FormField>
              )}
              {outwardItemType === ITEM_LINK_TYPE_VALUE.IQL && (
                <FormField name="outwardIql" label="">
                  {({ field }) => (
                    <TextArea {...field} rows={2} placeholder={t('pages.LinkModal.FormField.iqlPlaceholder')} />
                  )}
                </FormField>
              )}
            </>
          )}
        </Modal>
      )}
    </Formik>
  );
};

export default LinkModal;
